import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DataService } from './data/data.service';
import { GetProductPageQuery, GetProductPageQueryVariables } from '../../common/generated-types';
import { GET_PRODUCT_PAGE } from '../../common/graphql/documents.graphql';
import { NGXLogger } from 'ngx-logger';
import { StateService } from './state/state.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

const PRODUCT_RESOLVER_STORE_IDENTIFIER_KEY = makeStateKey<string>('PAGE_RESOLVER_STORE_IDENTIFIER');

@Injectable({ providedIn: 'root' })
export class ProductResolver implements Resolve<any> {
  productSlug: string;
  storeIdentifier: string;
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject('STORE_IDENTIFIER') public storeIdentifierFromSSR: string | null,
    private dataService: DataService,
    private stateService: StateService,
    private transferState: TransferState,
    private logger: NGXLogger,
    private router: Router
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    this.productSlug = route.paramMap.get('slug')?.toLowerCase() || '';
    if (isPlatformServer(this.platformId)) {
      this.storeIdentifier = this.storeIdentifierFromSSR || '';
      this.transferState.set(PRODUCT_RESOLVER_STORE_IDENTIFIER_KEY, this.storeIdentifier);
    }
    return this.stateService.select(state => state.storeIdentifier).pipe(
      switchMap(storeIdentifier => {
        if (isPlatformBrowser(this.platformId)) {
          if(!storeIdentifier) {
            this.storeIdentifier = this.transferState.get(PRODUCT_RESOLVER_STORE_IDENTIFIER_KEY, '');
            this.transferState.remove(PRODUCT_RESOLVER_STORE_IDENTIFIER_KEY);
          } else {
            this.storeIdentifier = storeIdentifier;
          }
        }
        this.logger.debug(`ProductResolver for ${this.productSlug} ${storeIdentifier}`);
        return this.dataService.query<GetProductPageQuery, GetProductPageQueryVariables>(GET_PRODUCT_PAGE, {
          identifier: this.storeIdentifier,
          slug: this.productSlug
        }).pipe(
          map(result => {
            this.logger.debug(`ProductResolver: result ${result}`);
            if (result?.productPage && result?.productPage?.productInfo !== null) {
              return result?.productPage;
            } else {
              this.logger.error(`ProductResolver: Product Page not found for slug '${this.productSlug}'`);
              this.router.navigateByUrl('/not-found', { skipLocationChange: true });
              return null;
            }
          }),
          catchError((err) => {
            // In case of errors, also go to not-found
            this.logger.error(`ProductResolver: Error ${err} fetching product page for slug '${this.productSlug}'`);
            this.router.navigateByUrl('/not-found', { skipLocationChange: true });
            return of(null);
          })
        )
      })
    );
  }
}
